// containers/Scanner_Form/Scanner_Form.jsx
//
// Visually matches TradingBotDashboard: standard MUI dark theme, Roboto font,
// blue primary accent, Paper variant="outlined" panels, rounded buttons,
// descriptive helper text. State, validation, payload, and API behavior preserved.

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Button, Tabs, Tab, Card, CardContent,
  TextField, Stepper, Step, StepLabel, Grid, Chip, Stack,
  IconButton, Tooltip, Collapse, Snackbar, Alert,
  Slider, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl, InputLabel, Divider,
} from '@mui/material';
import {
  AddCircleOutline, Settings, PlayArrow, Pause,
  Delete, Edit, KeyboardArrowDown, KeyboardArrowUp, Bolt,
  WarningAmber, AccountBalance, TrendingUp, ShowChart,
  AttachMoney, Timeline,
} from '@mui/icons-material';

import Toolbar from '../../components/Toolbar/Toolbar';
import Logout from '../../containers/Login/Logout';
import scannerApi from '../../services/scannerApi';
import ScannerTriggerBuilder from '../../components/ScannerTriggerBuilder';
import UniverseSelector from '../../components/UniverseSelector';
import LiveTriggerFeed from '../../components/LiveTriggerFeed';
import StrategyBuilder from '../../components/conditionBuilder';
import exchangeApiService from '../../services/exchangeApiService';
import {
  buildLogicFromConditions,
  extractIndicatorsFromConditions,
} from '../../components/strategyUtils';

const STEPS = ['Basics', 'Trigger', 'Strategy', 'Safety'];


/* ════════════════════════════════════════════════════════════════════════
   PAGE
   ════════════════════════════════════════════════════════════════════════ */

export default function ScannerDashboard() {
  const [activeTab, setActiveTab] = useState('list');
  const [profiles, setProfiles] = useState([]);
  const [spawnedByProfile, setSpawnedByProfile] = useState({});
  const [expandedProfile, setExpandedProfile] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const [isCreating, setIsCreating] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(makeDraft());
  const [userExchanges, setUserExchanges] = useState([]);

  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    exchangeApiService.list()
      .then(setUserExchanges)
      .catch(err => showToast('error', `Failed to load exchanges: ${err.message}`));
  }, []);

  async function refresh() {
    try {
      const p = await scannerApi.scannerProfiles.list();
      setProfiles(p || []);
      const spawnEntries = await Promise.all((p || []).map(async (prof) => {
        try {
          const bots = await scannerApi.scannerProfiles.spawned(prof.id, 50);
          return [prof.id, bots];
        } catch { return [prof.id, []]; }
      }));
      setSpawnedByProfile(Object.fromEntries(spawnEntries));
    } catch (err) {
      showToast('error', `Failed to load: ${err.message}`);
    }
  }

  function showToast(severity, message) { setToast({ open: true, severity, message }); }
  function startCreate() { setDraft(makeDraft()); setEditingId(null); setActiveStep(0); setIsCreating(true); setActiveTab('create'); }
  function startEdit(profile) { setDraft(profileToDraft(profile)); setEditingId(profile.id); setActiveStep(0); setIsCreating(true); setActiveTab('create'); }
  function cancelCreate() { setIsCreating(false); setEditingId(null); setDraft(makeDraft()); setActiveTab('list'); }

  async function handleSave(enabled = true) {
    const errors = validateDraft(draft);
    if (errors.length) { showToast('error', errors[0]); return; }
    let payload;
    try { payload = draftToPayload(draft, enabled); }
    catch (err) { showToast('error', err.message); return; }

    try {
      if (editingId) {
        await scannerApi.scannerProfiles.update(editingId, payload);
        showToast('success', 'Scanner updated');
      } else {
        await scannerApi.scannerProfiles.create(payload);
        showToast('success', enabled ? 'Scanner active' : 'Scanner saved (paused)');
      }
      await refresh();
      cancelCreate();
    } catch (err) {
      showToast('error', err?.response?.data?.error || err.message);
    }
  }

  async function handleToggle(profile) {
    try { await scannerApi.scannerProfiles.toggle(profile.id); await refresh(); }
    catch (err) { showToast('error', err.message); }
  }

  async function handleDelete(id) {
    try {
      await scannerApi.scannerProfiles.remove(id);
      setConfirmDeleteId(null);
      showToast('success', 'Scanner deleted');
      await refresh();
    } catch (err) { showToast('error', err.message); }
  }

  return (
    <>
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast(t => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      <Toolbar button={<Logout />} />

      <Box sx={{ maxWidth: '1200px', mx: 'auto', p: 3 }}>
        <Typography
          variant="h4"
          fontWeight={700}
          sx={{ color: 'text.primary', mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}
        >
          <Bolt fontSize="large" />
          Multi-Symbol Scanners
          <Chip
            size="small"
            label="Spot Only"
            variant="outlined"
            sx={{ ml: 1, fontWeight: 600 }}
          />
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{ mb: 3 }}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="My Scanners" value="list" icon={<Settings />} />
          <Tab label="Create Scanner" value="create" icon={<AddCircleOutline />} onClick={() => !isCreating && startCreate()} />
          <Tab label="Live Feed" value="feed" icon={<Bolt />} />
        </Tabs>

        {activeTab === 'list' && (
          <ProfileList
            profiles={profiles}
            spawnedByProfile={spawnedByProfile}
            expandedProfile={expandedProfile}
            onExpand={setExpandedProfile}
            onCreate={startCreate}
            onEdit={startEdit}
            onToggle={handleToggle}
            onDelete={(id) => setConfirmDeleteId(id)}
          />
        )}

        {activeTab === 'create' && isCreating && (
          <CreateForm
            draft={draft} setDraft={setDraft}
            activeStep={activeStep} setActiveStep={setActiveStep}
            editing={Boolean(editingId)}
            onCancel={cancelCreate} onSave={handleSave}
            userExchanges={userExchanges}
          />
        )}

        {activeTab === 'create' && !isCreating && (
          <Paper elevation={0} sx={{
            p: 4, textAlign: 'center',
            border: '1px solid #e0e0e0', borderRadius: 2,
          }}>
            <Typography variant="h6" gutterBottom>No active draft</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Start a new scanner to define a universe and trigger
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddCircleOutline />}
              onClick={startCreate}
              sx={{ bgcolor: 'grey.800', '&:hover': { bgcolor: 'grey.700' } }}
            >
              Create Scanner
            </Button>
          </Paper>
        )}

        {activeTab === 'feed' && (
          <LiveTriggerFeed profiles={profiles} spawnedBots={spawnedByProfile} />
        )}
      </Box>

      <Dialog
        open={Boolean(confirmDeleteId)}
        onClose={() => setConfirmDeleteId(null)}
        maxWidth="xs" fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmber color="warning" />
          Delete scanner?
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            This will stop scanning and delete the profile permanently. Bots already
            spawned will continue running until they exit normally.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<Delete />}
            onClick={() => handleDelete(confirmDeleteId)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}


/* ════════════════════════════════════════════════════════════════════════
   PROFILE LIST + CARD
   ════════════════════════════════════════════════════════════════════════ */

function ProfileList({ profiles, spawnedByProfile, expandedProfile, onExpand, onCreate, onEdit, onToggle, onDelete }) {
  if (profiles.length === 0) {
    return (
      <>
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddCircleOutline />}
            onClick={onCreate}
            sx={{ bgcolor: 'grey.800', '&:hover': { bgcolor: 'grey.700' } }}
          >
            Create New Scanner
          </Button>
        </Stack>
        <Paper elevation={0} sx={{
          p: 4, textAlign: 'center',
          border: '1px solid #e0e0e0', borderRadius: 2,
        }}>
          <Typography variant="h6" gutterBottom>No scanners created yet</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Get started by creating a scanner that watches a universe and auto-spawns bots when conditions trigger
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddCircleOutline />}
            onClick={onCreate}
            sx={{ bgcolor: 'grey.800', '&:hover': { bgcolor: 'grey.700' } }}
          >
            Create First Scanner
          </Button>
        </Paper>
      </>
    );
  }

  return (
    <>
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddCircleOutline />}
          onClick={onCreate}
          sx={{ bgcolor: 'grey.800', '&:hover': { bgcolor: 'grey.700' } }}
        >
          Create New Scanner
        </Button>
      </Stack>

      <Stack spacing={2}>
        {profiles.map(p => (
          <ProfileCard
            key={p.id}
            profile={p}
            spawnedBots={spawnedByProfile[p.id] || []}
            expanded={expandedProfile === p.id}
            onExpand={() => onExpand(expandedProfile === p.id ? null : p.id)}
            onEdit={() => onEdit(p)}
            onToggle={() => onToggle(p)}
            onDelete={() => onDelete(p.id)}
          />
        ))}
      </Stack>
    </>
  );
}


function ProfileCard({ profile, spawnedBots, expanded, onExpand, onEdit, onToggle, onDelete }) {
  const scannerCfg = useMemo(() => extractScannerCfg(profile), [profile]);
  const tradeInfo = useMemo(() => parseTradeInfo(profile.trade_info), [profile.trade_info]);
  const triggerSummary = useMemo(() => summarizeTrigger(scannerCfg.trigger), [scannerCfg.trigger]);
  const universeSummary = useMemo(() => summarizeUniverse(scannerCfg.universe), [scannerCfg.universe]);
  const enabled = (profile.status === 'scanner_profile') && !scannerCfg.paused;
  const isFutures = tradeInfo.marketType === 'futures';

  return (
    <Card sx={{
      borderRadius: 2,
      border: '1px solid #e0e0e0',
      '&:hover': { boxShadow: 2 },
    }}>
      <CardContent sx={{ p: '16px !important' }}>
        <Box
          onClick={onExpand}
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton size="small">
              {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>{profile.name}</Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label="Scanner Profile"
                  color="primary"
                  variant="outlined"
                  icon={<Bolt sx={{ fontSize: 14 }} />}
                />
                {!enabled && (
                  <Chip
                    size="small"
                    label="Paused"
                    color="warning"
                    icon={<Pause sx={{ fontSize: 14 }} />}
                  />
                )}
                <Chip
                  size="small"
                  label={isFutures ? `Futures ${tradeInfo.futures?.leverage || 1}×` : 'Spot'}
                  color={isFutures ? 'warning' : 'default'}
                  variant="outlined"
                />
                <Typography variant="body2" color="text.secondary">
                  {universeSummary} • {triggerSummary}
                </Typography>
              </Stack>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`${spawnedBots.length} spawned`}
              size="small"
              variant="outlined"
            />
            <Tooltip title={enabled ? 'Pause Scanner' : 'Activate Scanner'}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onToggle(); }}
                color={enabled ? 'warning' : 'success'}
              >
                {enabled ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ mt: 3, pl: 2, pr: 1 }}>
            <Grid container spacing={3}>
              {/* TRIGGER */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Bolt color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>Trigger</Typography>
                  </Box>
                  <Stack spacing={1.5}>
                    <KV label="Type" value={scannerCfg.trigger?._preset?.kind || scannerCfg.trigger?.type || 'expression'} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Expression</Typography>
                      <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: 'background.default' }}>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-word' }}>
                          {scannerCfg.trigger?.expression || triggerSummary}
                        </Typography>
                      </Paper>
                    </Box>
                    <Stack direction="row" spacing={3}>
                      <KV label="Cooldown" value={fmtSeconds(scannerCfg.cooldown_seconds)} sub="per symbol" />
                      <KV label="Interval" value={scannerCfg.primary_interval || tradeInfo.timeframe || '—'} />
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>

              {/* UNIVERSE */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <ShowChart color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>Universe</Typography>
                  </Box>
                  <Stack spacing={1.5}>
                    <KV label="Type" value={scannerCfg.universe?.type || '—'} />
                    {scannerCfg.universe?.type === 'top_volume' && (
                      <Stack direction="row" spacing={3}>
                        <KV label="Limit" value={scannerCfg.universe?.limit || 100} />
                        <KV label="Quote" value={scannerCfg.universe?.quote || 'USDT'} />
                        <KV label="Min Volume" value={fmtMoneyShort(scannerCfg.universe?.min_volume) || '0'} />
                      </Stack>
                    )}
                    {scannerCfg.universe?.type === 'list' && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Symbols ({(scannerCfg.universe?.symbols || []).length})
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: 'background.default', maxHeight: 80, overflow: 'auto' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.6 }}>
                            {(scannerCfg.universe?.symbols || []).join(', ') || '—'}
                          </Typography>
                        </Paper>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              </Grid>

              {/* POSITION SIZING */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AttachMoney color="primary" />
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <KV
                        label={isFutures ? 'Margin' : 'Quote'}
                        value={fmtMoneyShort(tradeInfo.totalInvestment ?? tradeInfo.value)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <KV label="Market" value={isFutures ? 'Futures' : 'Spot'} />
                    </Grid>
                    {isFutures && (
                      <>
                        <Grid item xs={6}>
                          <KV label="Leverage" value={`${tradeInfo.futures?.leverage || 1}×`} />
                        </Grid>
                        <Grid item xs={6}>
                          <KV
                            label="Margin Mode"
                            value={(tradeInfo.futures?.marginType || tradeInfo.futures?.marginMode || 'cross').toLowerCase()}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <KV
                            label="Position Mode"
                            value={tradeInfo.futures?.positionSide === 'hedge' ? 'Hedge' : 'One-Way'}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Paper>
              </Grid>

              {/* RISK + LIMITS */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <WarningAmber color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>Risk &amp; Limits</Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}><KV label="Take Profit" value={tradeInfo.profitStop || '—'} /></Grid>
                    <Grid item xs={6}><KV label="Stop Loss" value={tradeInfo.lossStop || '—'} /></Grid>
                    <Grid item xs={6}><KV label="Trailing" value={tradeInfo.trailingStop ? `${tradeInfo.trailingStop}%` : '—'} /></Grid>
                    <Grid item xs={6}><KV label="Max Lifetime" value={scannerCfg.max_lifetime_seconds ? fmtSeconds(scannerCfg.max_lifetime_seconds) : 'None'} /></Grid>
                    <Grid item xs={6}><KV label="Max Concurrent" value={scannerCfg.max_concurrent_spawned || '∞'} /></Grid>
                    <Grid item xs={6}>
                      <KV
                        label="Capital Cap"
                        value={scannerCfg.total_capital_cap ? `$${Number(scannerCfg.total_capital_cap).toLocaleString()}` : 'Unlimited'}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* SPAWNED */}
              {spawnedBots.length > 0 && (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{
                      px: 2.5, py: 1.5,
                      bgcolor: 'background.default',
                      borderBottom: '1px solid', borderColor: 'divider',
                      display: 'flex', alignItems: 'center', gap: 1,
                    }}>
                      <Timeline color="primary" fontSize="small" />
                      <Typography variant="subtitle2" fontWeight={600}>Recent Spawns</Typography>
                      <Chip size="small" variant="outlined" label={`${spawnedBots.length} total`} sx={{ ml: 'auto' }} />
                    </Box>
                    <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {spawnedBots.slice(0, 10).map(b => (
                        <Box
                          key={b.id}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '160px 120px 1fr 100px',
                            gap: 2, alignItems: 'center', px: 2.5, py: 1,
                            borderBottom: '1px solid', borderColor: 'divider',
                            '&:last-child': { borderBottom: 'none' },
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {b.createdAt ? new Date(b.createdAt).toLocaleString() : (b.created_at ? new Date(b.created_at).toLocaleString() : '—')}
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {(() => { try { return parseTradeInfo(b.trade_info)?.symbol || '—'; } catch { return '—'; } })()}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {b.name}
                          </Typography>
                          <Chip
                            size="small"
                            label={b.status || 'running'}
                            color={b.status === 'terminated' ? 'default' : 'success'}
                            variant="outlined"
                          />
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              )}
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3, mb: 1 }}>
              <Button variant="outlined" size="small" startIcon={<Edit />} onClick={onEdit}>
                Edit
              </Button>
              <Button variant="outlined" size="small" color="error" startIcon={<Delete />} onClick={onDelete}>
                Delete
              </Button>
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}


/* ════════════════════════════════════════════════════════════════════════
   CREATE FORM + STEPS
   ════════════════════════════════════════════════════════════════════════ */

function CreateForm({ draft, setDraft, activeStep, setActiveStep, editing, onCancel, onSave, userExchanges }) {
  const patch = (fields) => setDraft(d => ({ ...d, ...fields }));

  return (
    <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 4, border: '1px solid #e0e0e0' }}>
      <CardContent>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {STEPS.map(label => (<Step key={label}><StepLabel>{label}</StepLabel></Step>))}
        </Stepper>

        {activeStep === 0 && <BasicsStep draft={draft} patch={patch} userExchanges={userExchanges} />}
        {activeStep === 1 && (
          <ScannerTriggerBuilder
            trigger={draft.trigger}
            onTriggerChange={(t) => patch({ trigger: t })}
            cooldownSeconds={draft.cooldown_seconds}
            onCooldownChange={(s) => patch({ cooldown_seconds: s })}
            marketType={draft.market_type}
            interval={draft.primary_interval}
            onIntervalChange={(i) => patch({ primary_interval: i })}
          />
        )}
        {activeStep === 2 && <StrategyStep draft={draft} patch={patch} />}
        {activeStep === 3 && <SafetyStep draft={draft} patch={patch} />}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button onClick={activeStep === 0 ? onCancel : () => setActiveStep(s => s - 1)} variant="outlined">
            {activeStep === 0 ? 'Cancel' : 'Back'}
          </Button>
          <Box>
            {activeStep < STEPS.length - 1 && (
              <Button
                onClick={() => setActiveStep(s => s + 1)}
                variant="contained"
                sx={{ ml: 1, bgcolor: 'grey.800', '&:hover': { bgcolor: 'grey.700' } }}
              >
                Next
              </Button>
            )}
            {activeStep === STEPS.length - 1 && (
              <>
                <Button
                  onClick={() => onSave(false)}
                  variant="outlined"
                  sx={{ ml: 1, borderColor: 'grey.600', color: 'grey.300', '&:hover': { borderColor: 'grey.500', bgcolor: 'rgba(255,255,255,0.05)' } }}
                >
                  Save (Paused)
                </Button>
                <Button
                  onClick={() => onSave(true)}
                  variant="contained"
                  sx={{ ml: 1, bgcolor: 'grey.900', '&:hover': { bgcolor: 'grey.800' } }}
                >
                  Save & Activate
                </Button>
              </>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}


function BasicsStep({ draft, patch, userExchanges }) {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Scanner Name"
          value={draft.name}
          onChange={e => patch({ name: e.target.value })}
          fullWidth
          placeholder="e.g. Top 100 Momentum Scanner"
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Exchange</InputLabel>
          <Select
            value={draft.exchange_id ?? ''}
            onChange={e => patch({ exchange_id: e.target.value || null })}
            label="Exchange"
          >
            <MenuItem value="" disabled>Select an exchange…</MenuItem>
            {(userExchanges || []).map(ex => (
              <MenuItem key={ex.id} value={ex.id}>
                {ex.name || ex.exchange}{ex.simulated ? ' (Simulated)' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>


      <Grid item xs={12}>
        <Paper
          variant="outlined"
          sx={{
            p: 2, borderRadius: 2,
            display: 'flex', alignItems: 'center', gap: 1.5,
            bgcolor: 'action.hover',
          }}
        >
          <AccountBalance color="primary" fontSize="small" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              Spot Market
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This scanner watches spot pairs only. Spawned bots trade without leverage.
            </Typography>
          </Box>
          <Chip size="small" label="Spot" color="primary" variant="outlined" />
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <UniverseSelector
          universe={draft.universe_filter}
          onUniverseChange={(u) => patch({ universe_filter: u })}
          marketType={draft.market_type}
        />
      </Grid>
    </Grid>
  );
}


function StrategyStep({ draft, patch }) {
  const isFutures = draft.market_type === 'futures';

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>Position Sizing</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>per spawned bot</Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={isFutures ? 6 : 12}>
              <TextField
                label={isFutures ? 'Margin Per Bot (USDT)' : 'Quote Per Bot (USDT)'}
                type="number"
                value={draft.position_size}
                onChange={e => patch({ position_size: Number(e.target.value) || 0 })}
                fullWidth
                placeholder="e.g. 50"
                inputProps={{ step: 5, min: 0 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}

              />
            </Grid>

            {isFutures && (
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Leverage: <strong>{draft.leverage}×</strong>
                </Typography>
                <Slider
                  value={draft.leverage}
                  onChange={(_, v) => patch({ leverage: v })}
                  min={1}
                  max={50}
                  step={1}
                  marks={[
                    { value: 1, label: '1×' },
                    { value: 10, label: '10×' },
                    { value: 25, label: '25×' },
                    { value: 50, label: '50×' },
                  ]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={v => `${v}×`}
                  sx={{
                    color: draft.leverage > 20 ? 'error.main'
                      : draft.leverage > 10 ? 'warning.main'
                        : 'primary.main',
                  }}
                />
              </Grid>
            )}
          </Grid>

          {isFutures && (
            <>
              <Divider sx={{ my: 2.5 }} />
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Margin Mode</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Paper
                  variant="outlined"
                  onClick={() => patch({ margin_mode: 'ISOLATED' })}
                  sx={{
                    flex: 1, p: 2, cursor: 'pointer',
                    borderColor: draft.margin_mode === 'ISOLATED' ? 'primary.main' : 'divider',
                    borderWidth: draft.margin_mode === 'ISOLATED' ? 2 : 1,
                    bgcolor: draft.margin_mode === 'ISOLATED' ? 'action.selected' : 'background.paper',
                    '&:hover': { borderColor: 'primary.light' },
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600}>Isolated</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Per-position margin (safer)
                  </Typography>
                </Paper>
                <Paper
                  variant="outlined"
                  onClick={() => patch({ margin_mode: 'CROSSED' })}
                  sx={{
                    flex: 1, p: 2, cursor: 'pointer',
                    borderColor: draft.margin_mode === 'CROSSED' ? 'primary.main' : 'divider',
                    borderWidth: draft.margin_mode === 'CROSSED' ? 2 : 1,
                    bgcolor: draft.margin_mode === 'CROSSED' ? 'action.selected' : 'background.paper',
                    '&:hover': { borderColor: 'primary.light' },
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600}>Cross</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Shared account margin
                  </Typography>
                </Paper>
              </Box>
            </>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <StrategyBuilder
          buyConditions={draft.buy_conditions}
          sellConditions={draft.sell_conditions}
          onBuyConditionsChange={(c) => patch({ buy_conditions: c })}
          onSellConditionsChange={(c) => patch({ sell_conditions: c })}
          shortOpenConditions={draft.short_open_conditions}
          shortCloseConditions={draft.short_close_conditions}
          onShortOpenConditionsChange={(c) => patch({ short_open_conditions: c })}
          onShortCloseConditionsChange={(c) => patch({ short_close_conditions: c })}
          marketType={draft.market_type}
          positionMode={draft.position_mode}
          profitStop={draft.profit_stop_pct}
          lossStop={draft.loss_stop_pct}
          onProfitStopChange={(v) => patch({ profit_stop_pct: v })}
          onLossStopChange={(v) => patch({ loss_stop_pct: v })}
          interval={draft.primary_interval}
        />
      </Grid>

      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Additional Controls</Typography>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Trailing Stop (%)"
                type="number"
                value={draft.trailing_stop_pct ?? ''}
                onChange={e => patch({ trailing_stop_pct: e.target.value ? Number(e.target.value) : null })}
                fullWidth
                placeholder="None"
                inputProps={{ step: 0.1, min: 0 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Max Bot Lifetime (minutes)"
                type="number"
                value={draft.max_lifetime_minutes ?? ''}
                onChange={e => patch({ max_lifetime_minutes: e.target.value ? Number(e.target.value) : null })}
                fullWidth
                placeholder="No limit"
                inputProps={{ step: 5, min: 0 }}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
}


function SafetyStep({ draft, patch }) {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>Safety Limits</Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Max Concurrent Bots: <strong>{draft.max_concurrent_spawned}</strong>
            </Typography>
            <Slider
              value={draft.max_concurrent_spawned}
              onChange={(_, v) => patch({ max_concurrent_spawned: v })}
              min={1}
              max={20}
              step={1}
              marks={[
                { value: 1, label: '1' },
                { value: 5, label: '5' },
                { value: 10, label: '10' },
                { value: 20, label: '20' },
              ]}
              valueLabelDisplay="auto"
            />

          </Box>

          <TextField
            label="Total Capital Cap (USDT)"
            type="number"
            required
            value={draft.total_capital_cap ?? ''}
            onChange={e => patch({ total_capital_cap: e.target.value ? Number(e.target.value) : null })}
            fullWidth
            placeholder="e.g. 500"
            inputProps={{ step: 50, min: 0, max: 1000 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            error={draft.total_capital_cap != null && Number(draft.total_capital_cap) > 1000}
            helperText={
              draft.total_capital_cap != null && Number(draft.total_capital_cap) > 1000
                ? 'Beta limit: maximum $1000'
                : 'Sum of position sizing across all spawned bots will not exceed this. Beta limit: $1000.'
            }
          />

          <Alert severity="info" sx={{ mt: 2 }}>
            <strong>Beta limit:</strong> Total capital cap is currently restricted to $1000 across all spawned bots.
            Your exchange balance will be verified when the scanner is activated.
          </Alert>
        </Paper>
      </Grid>
    </Grid>
  );
}


/* ════════════════════════════════════════════════════════════════════════
   Tiny shared display primitive
   ════════════════════════════════════════════════════════════════════════ */

function KV({ label, value, sub }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>
        {value ?? '—'}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}


/* ════════════════════════════════════════════════════════════════════════
   PARSE / FORMAT HELPERS
   ════════════════════════════════════════════════════════════════════════ */

function parseTradeInfo(raw) {
  if (raw == null) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return {}; }
}

function extractScannerCfg(profile) {
  return parseTradeInfo(profile.trade_info).scanner || {};
}

function fmtSeconds(s) {
  if (!Number.isFinite(s)) return '—';
  if (s >= 3600) return `${(s / 3600).toFixed(1)}h`;
  if (s >= 60) return `${Math.round(s / 60)}m`;
  return `${s}s`;
}

function fmtMoneyShort(v) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}

function summarizeUniverse(u) {
  if (!u) return '—';
  if (u.type === 'top_volume') return `Top ${u.limit || 100} by ${u.quote || 'USDT'} vol`;
  if (u.type === 'list') return `${(u.symbols || []).length} symbols`;
  if (u.type === 'category') return `Category: ${u.category}`;
  return u.type;
}

function summarizeTrigger(t) {
  if (!t) return '—';
  if (t._preset?.kind === 'volume_spike') {
    return `Vol ≥${t._preset.multiplier}× over ${t._preset.window_seconds}s`;
  }
  if (t.type === 'expression') return t.expression || `${(t.conditions || []).length} conditions`;
  if (t.type === 'volume_spike') return `Vol ≥${t.config?.multiplier || 3}× over ${t.config?.window_seconds || 60}s`;
  return t.type;
}


/* ════════════════════════════════════════════════════════════════════════
   DRAFT / PAYLOAD — unchanged logic
   ════════════════════════════════════════════════════════════════════════ */

function makeDraft() {
  return {
    name: '', description: '', exchange_id: null,
    market_type: 'spot', position_mode: 'one_way',
        universe_filter: {
      type: 'top_volume',
      limit: 100,
      quote: 'USDT',
      min_volume: 0,
      fee_filter: 'any',   // ← add
      blacklist: [],
    },
    trigger: { type: 'expression', conditions: [] },
    primary_interval: '5m', cooldown_seconds: 600,
    buy_conditions: [], sell_conditions: [],
    short_open_conditions: [], short_close_conditions: [],
    profit_stop_pct: '', loss_stop_pct: '',
    trailing_stop_pct: null, max_lifetime_minutes: null,
    position_size: 50, leverage: 5, margin_mode: 'ISOLATED',
    max_concurrent_spawned: 3, total_capital_cap: null,
  };
}

function profileToDraft(profile) {
  const ti = parseTradeInfo(profile.trade_info);
  const scannerCfg = ti.scanner || {};
  const futures = ti.futures || {};
  const original = scannerCfg._original_conditions || {};

  return {
    name: profile.name || '',
    description: scannerCfg.description || '',
    exchange_id: profile.exchange_id ?? null,
    market_type: ti.marketType || 'spot',
    position_mode: futures.positionSide === 'hedge' ? 'hedge' : 'one_way',
    universe_filter: scannerCfg.universe || { type: 'top_volume', limit: 100, quote: 'USDT' },
    trigger: reconstructTriggerFromBackend(scannerCfg.trigger),
    primary_interval: scannerCfg.primary_interval || ti.timeframe || '5m',
    cooldown_seconds: scannerCfg.cooldown_seconds ?? 600,
    buy_conditions: original.buy_conditions || [],
    sell_conditions: original.sell_conditions || [],
    short_open_conditions: original.short_open_conditions || [],
    short_close_conditions: original.short_close_conditions || [],
    profit_stop_pct: stripPct(ti.profitStop),
    loss_stop_pct: stripPct(ti.lossStop),
    trailing_stop_pct: ti.trailingStop ? Number(ti.trailingStop) : null,
    max_lifetime_minutes: scannerCfg.max_lifetime_seconds ? Math.floor(scannerCfg.max_lifetime_seconds / 60) : null,
    position_size: ti.totalInvestment ?? ti.value ?? 50,
    leverage: futures.leverage ?? 5,
    margin_mode: futures.marginType ?? 'ISOLATED',
    max_concurrent_spawned: scannerCfg.max_concurrent_spawned ?? 3,
    total_capital_cap: scannerCfg.total_capital_cap ?? null,
  };
}

function reconstructTriggerFromBackend(savedTrigger) {
  if (!savedTrigger) return { type: 'expression', conditions: [] };
  if (savedTrigger._preset?.kind === 'volume_spike') {
    return {
      type: 'volume_spike',
      config: {
        window_seconds: savedTrigger._preset.window_seconds,
        baseline_seconds: savedTrigger._preset.baseline_seconds,
        multiplier: savedTrigger._preset.multiplier,
        min_recent_volume: savedTrigger._preset.min_recent_volume,
      },
    };
  }
  if (savedTrigger.type === 'expression') {
    return { type: 'expression', conditions: Array.isArray(savedTrigger.conditions) ? savedTrigger.conditions : [] };
  }
  if (savedTrigger.type === 'volume_spike') return savedTrigger;
  return { type: 'expression', conditions: [] };
}

function stripPct(v) {
  if (v == null) return '';
  if (typeof v === 'number') return v;
  return String(v).replace(/[^\d.]/g, '');
}

function draftToPayload(d, enabled) {
  const isFutures = d.market_type === 'futures';
  const isHedge = isFutures && d.position_mode === 'hedge';
  const trigger = translateTriggerForBackend(d.trigger);
  const buy_logic = buildLogicFromConditions(d.buy_conditions);
  const sell_logic = buildLogicFromConditions(d.sell_conditions);
  const shortOpenLogic = isHedge ? buildLogicFromConditions(d.short_open_conditions) : null;
  const shortCloseLogic = isHedge ? buildLogicFromConditions(d.short_close_conditions) : null;

  const trade_info = {
    timeframe: d.primary_interval, symbol: null, marketType: d.market_type,
    totalInvestment: d.position_size,
    profitStop: d.profit_stop_pct ? `${d.profit_stop_pct}%` : null,
    lossStop: d.loss_stop_pct ? `${d.loss_stop_pct}%` : null,
    trailingStop: d.trailing_stop_pct ?? null,
    indicators: extractIndicatorsFromConditions([
      ...d.buy_conditions, ...d.sell_conditions,
      ...(isHedge ? d.short_open_conditions : []),
      ...(isHedge ? d.short_close_conditions : []),
    ]),
    orderSizing: { mode: 'quote', value: d.position_size, assetType: 'quote' },
    futures: isFutures ? {
      type: 'usdt_m', leverage: d.leverage, marginType: d.margin_mode,
      positionSide: d.position_mode,
      ...(isHedge && { shortOpenLogic, shortCloseLogic }),
    } : null,
    scanner: {
      trigger, universe: d.universe_filter,
      primary_interval: d.primary_interval, cooldown_seconds: d.cooldown_seconds,
      max_concurrent_spawned: d.max_concurrent_spawned,
      total_capital_cap: d.total_capital_cap,
      max_lifetime_seconds: d.max_lifetime_minutes ? d.max_lifetime_minutes * 60 : null,
      description: d.description,
      _original_conditions: {
        buy_conditions: d.buy_conditions, sell_conditions: d.sell_conditions,
        short_open_conditions: isHedge ? d.short_open_conditions : [],
        short_close_conditions: isHedge ? d.short_close_conditions : [],
      },
      paused: !enabled,
    },
  };

  return {
    name: d.name, exchange_id: d.exchange_id,
    buy_logic, sell_logic, trade_info, status: 'scanner_profile',
  };
}

function translateTriggerForBackend(rawTrigger) {
  const t = rawTrigger || {};
  if (t.type === 'expression' || !t.type) {
    const conditions = Array.isArray(t.conditions) ? t.conditions : [];
    const expression = buildLogicFromConditions(conditions);
    if (!expression || !expression.trim()) throw new Error('Expression trigger has no conditions (Step 2)');
    return { type: 'expression', expression, indicators: extractIndicatorsFromConditions(conditions), conditions };
  }
  if (t.type === 'volume_spike') {
    const cfg = t.config || {};
    const win = Number(cfg.window_seconds) > 0 ? Number(cfg.window_seconds) : 60;
    const base = Number(cfg.baseline_seconds) > 0 ? Number(cfg.baseline_seconds) : 1800;
    const mult = Number(cfg.multiplier) > 0 ? Number(cfg.multiplier) : 3;
    const minVol = Number(cfg.min_recent_volume) > 0 ? Number(cfg.min_recent_volume) : 0;
    let expression = `tickVolumeSpikeRatio(${win},${base}) >= ${mult}`;
    const indicators = [{ name: 'tickVolumeSpikeRatio', windowSec: win, baselineSec: base }];
    if (minVol > 0) {
      expression += ` AND tickVolumeSum(${win}) >= ${minVol}`;
      indicators.push({ name: 'tickVolumeSum', windowSec: win });
    }
    return {
      type: 'expression', expression, indicators, conditions: [],
      _preset: { kind: 'volume_spike', window_seconds: win, baseline_seconds: base, multiplier: mult, min_recent_volume: minVol },
    };
  }
  if (t.type === 'liquidation_cascade') throw new Error('Liquidation cascade trigger is not yet supported');
  throw new Error(`Unknown trigger type: ${t.type}`);
}

function validateDraft(d) {
  const errors = [];
  if (!d.name?.trim()) errors.push('Scanner name is required');
  if (!d.exchange_id) errors.push('Please select an exchange (Step 1)');

  const t = d.trigger || {};
  if (t.type === 'expression' || !t.type) {
    if (!Array.isArray(t.conditions) || t.conditions.length === 0) errors.push('Add at least one trigger condition (Step 2)');
  } else if (t.type === 'volume_spike') {
    const cfg = t.config || {};
    if (!(Number(cfg.window_seconds) > 0)) errors.push('Volume Spike: window must be positive (Step 2)');
    if (!(Number(cfg.baseline_seconds) > 0)) errors.push('Volume Spike: baseline must be positive (Step 2)');
    if (!(Number(cfg.multiplier) > 0)) errors.push('Volume Spike: multiplier must be positive (Step 2)');
  } else if (t.type === 'liquidation_cascade') {
    errors.push('Liquidation Cascade triggers are not yet supported (Step 2)');
  }

  if (d.universe_filter?.type === 'list' && (!d.universe_filter.symbols || d.universe_filter.symbols.length === 0)) {
    errors.push('Add at least one symbol to the universe (Step 1)');
  }

  const isHedge = d.market_type === 'futures' && d.position_mode === 'hedge';
  if (!d.buy_conditions?.length) errors.push(isHedge ? 'Add at least one Open Long condition (Step 3)' : 'Add at least one buy/entry condition (Step 3)');
  if (!d.sell_conditions?.length) errors.push(isHedge ? 'Add at least one Close Long condition (Step 3)' : 'Add at least one sell/exit condition (Step 3)');
  if (isHedge && !d.short_open_conditions?.length) errors.push('Add at least one Open Short condition (Step 3)');
  if (isHedge && !d.short_close_conditions?.length) errors.push('Add at least one Close Short condition (Step 3)');
  if (!d.position_size || Number(d.position_size) <= 0) errors.push('Position size must be greater than 0 (Step 3)');

  // Beta cap: total capital across all spawned bots
  const BETA_CAPITAL_CAP = 1000;
  const cap = Number(d.total_capital_cap);
  if (!cap || cap <= 0) {
    errors.push(`During beta, Total Capital Cap is required and must be ≤ $${BETA_CAPITAL_CAP} (Step 4)`);
  } else if (cap > BETA_CAPITAL_CAP) {
    errors.push(`During beta, Total Capital Cap cannot exceed $${BETA_CAPITAL_CAP} (Step 4)`);
  }

  return errors;
}